import Logger from '@/utils/logger';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Check if analytics is available
const isAnalyticsAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Performance-optimized GA initialization with error handling
export const initGA = (): void => {
  try {
    // Only initialize if we're in a browser environment
    if (typeof window === 'undefined') return;
    
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    
    // Define gtag function with performance optimization
    window.gtag = function gtag(...args: any[]) {
      try {
        // Use requestIdleCallback for better performance
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            window.dataLayer.push(args);
          });
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            window.dataLayer.push(args);
          }, 0);
        }
      } catch (error) {
        // Silent failure - analytics should not break the app
      }
    };

    // Configure GA4 with optimized settings
    window.gtag('js', new Date());
    window.gtag('config', 'G-JYRGFF839F', {
      // Performance optimizations
      send_page_view: false, // We'll handle page views manually
      anonymize_ip: true, // GDPR compliance
      allow_google_signals: false, // Improve performance
      allow_ad_personalization_signals: false, // Privacy friendly
    });

    // Only log in development
    if (import.meta.env.DEV) {
      Logger.debug('Google Analytics initialized successfully');
    }
  } catch (error) {
    // Silent failure - analytics should not break the app
    if (import.meta.env.DEV) {
      Logger.warn('Failed to initialize Google Analytics:', error);
    }
  }
};

// Optimized page view tracking with debouncing
let pageViewTimeout: NodeJS.Timeout | null = null;

export const trackPageView = (path: string): void => {
  try {
    // Clear any pending page view tracking
    if (pageViewTimeout) {
      clearTimeout(pageViewTimeout);
    }

    // Debounce page view tracking to avoid rapid consecutive calls
    pageViewTimeout = setTimeout(() => {
      if (isAnalyticsAvailable()) {
        window.gtag('config', 'G-JYRGFF839F', {
          page_path: path,
          page_title: document.title,
        });
        
        // Custom page view event for better tracking
        window.gtag('event', 'page_view', {
          page_path: path,
          page_title: document.title,
          page_location: window.location.href,
        });

        // Only log in development
        if (import.meta.env.DEV) {
          Logger.debug(`Page view tracked: ${path}`);
        }
      }
    }, 100);
  } catch (error) {
    // Silent failure in production
  }
};

// Enhanced event tracking with error handling
export const trackEvent = (
  action: string, 
  category: string, 
  label?: string, 
  value?: number
): void => {
  try {
    if (isAnalyticsAvailable()) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        // Add custom parameters for better analysis
        timestamp: Date.now(),
        user_agent: navigator.userAgent,
      });

      // Only log in development
      if (import.meta.env.DEV) {
        Logger.debug(`Event tracked: ${action} - ${category}${label ? ` - ${label}` : ''}`);
      }
    }
  } catch (error) {
    // Silent failure in production
  }
};

// Track user interactions for performance insights
export const trackUserTiming = (name: string, value: number, category = 'Performance'): void => {
  try {
    if (isAnalyticsAvailable()) {
      window.gtag('event', 'timing_complete', {
        name: name,
        value: value,
        event_category: category,
      });
    }
  } catch (error) {
    // Silent failure in production
  }
};

// Track errors for better debugging (development only)
export const trackError = (error: string, fatal = false): void => {
  try {
    if (isAnalyticsAvailable() && import.meta.env.DEV) {
      window.gtag('event', 'exception', {
        description: error,
        fatal: fatal,
      });
    }
  } catch (err) {
    // Silent failure in production
  }
};
